import React, { type JSX, useEffect } from "react";
import { t } from "ttag";

import { getProductURL } from "../../../api/products";
import { IProduct } from "../../../models/catalogue";
import {
    IImageURL,
    IProductID,
    IProductSKU,
    ISafeHTML,
    IWebPageURL,
} from "../../../models/nominals";
import { IAPIPrice } from "../../../models/prices";
import { VariantDescriptionBlock } from "../../../models/streamfield-blocks";
import {
    trackConfiguratorLoadedEvent,
    trackListrackProductBrowse,
    trackProductValuePageView,
    trackVariantSelectedEvent,
    trackViewItemEvent,
} from "../../../utils/analytics";
import { PriceType, getProductPrice } from "../../../utils/catalogue";
import { decimal } from "../../../utils/format";
import { isZipLocation } from "../../../utils/guards";
import { ZERO } from "../../../utils/money";
import { usePageSetting } from "../../../utils/settings";
import { navigateToURL } from "../../../utils/urls";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { addProductToWishList } from "../actions";
import { Configurator as Component } from "../components/Configurator";
import { LIST_FAVORITES, VIEW_FAVORITES } from "../constants";
import { defaults } from "../defaults";
import { showFinanceBlock } from "../elements/ConfiguratorPanelTrigger";
import {
    getSelectedUpgradeIDFromURL,
    pushPDPVariantURLChange,
    startHistoryListener,
} from "../history";
import {
    IModularConfiguratorOptionSet,
    IUpsellModalComponentFn,
} from "../models";
import {
    setAddToBasketBtnState,
    setAddToFavoritesBtnState,
    setSelectedRootProduct,
    setSelectedUpgrade,
} from "../reducers";
import {
    addonVariantPricesSelector,
    addonVariantsSelector,
    baseVariantSelector,
    rootProductSelector,
    upgradedVariantPriceSelector,
    upgradedVariantSelector,
} from "../selectors";
import {
    loadConcreteBundles,
    loadFinancingPlans,
    loadLocation,
    loadPrice,
    loadProducts,
    loadShippingMethod,
} from "../thunks";
import { isPreorderProduct } from "../utils";

export interface IProps {
    optionSet: IModularConfiguratorOptionSet;

    // CMS Page Data
    title?: string;
    preheader?: string;
    subhead?: string;
    description?: string;
    descriptions?: VariantDescriptionBlock[];
    promoCopy?: string;
    liveChatHeader?: string;
    chatLinkComponent?: JSX.Element | null;

    showImage?: boolean;
    showShippingLead?: boolean;

    // Site Page URLs for links and redirects
    financingLink: IWebPageURL;
    basketLink: IWebPageURL;
    favoritesLink: IWebPageURL;
    configureGiftsLink: IWebPageURL;

    // Optional: Up Sell Modal functionality
    showUpsellModal?: (product: IProduct | null) => boolean;
    showUpsellModalIcon?: boolean | false;
    getUpsellModalComponentFn?: (
        product: IProduct,
    ) => IUpsellModalComponentFn | null;

    // Colors
    buttonColor?: string;

    // Behavior options
    enableHistoryInteraction?: boolean;

    // Misc UI options
    buildAdditionalContent?: (
        rootProduct: IProduct | null,
        optionSet: IModularConfiguratorOptionSet,
    ) => JSX.Element | null;
    configType?: string;
    getDeliveryCopy?: (
        rootProduct: IProduct,
        isPreorder: boolean,
    ) => string | null;
    getDeliveryIcon?: () => IImageURL | null;
    showFeelScale?: boolean;
    showPreTitle?: boolean;

    learnMoreSelector?: string;
    hideLearnMoreLink?: boolean;

    showVariantCallout?: boolean;

    starRatingURL?: IWebPageURL;
    strikeThroughMSRP?: boolean;
    actualPriceStyle?: string;
    boxHeaderContent?: string;
    topBadgeContent?: ISafeHTML | string;
    overrideFinancingCopy?: string;
    supDollarSign?: boolean;

    enableAddToFavorites?: boolean;
    showStarRatingsModal?: boolean;
    showFinancingModal?: boolean;
    phoneNumber?: string;
    showImagePerUpgradeOption?: boolean;

    isPlpPanelStyle?: boolean;
}

// Helper functions extracted from component
const getRootProductID = (
    rootProduct: IProduct | null,
    optionSet: IModularConfiguratorOptionSet,
): IProductID => {
    return rootProduct ? rootProduct.id : optionSet.product_ids[0];
};

const getCurrentProductID = (
    rootProduct: IProduct | null,
    optionSet: IModularConfiguratorOptionSet,
): IProductID => {
    return rootProduct ? rootProduct.id : optionSet.product_ids[0];
};

const getDescription = (
    descriptions: VariantDescriptionBlock[] | undefined,
    description: string | undefined,
    productID: IProductID,
): string | undefined => {
    if (descriptions && descriptions.length) {
        const match = descriptions.find(({ product }) => {
            return product === productID;
        });
        if (match && match.description) {
            return match.description;
        }
    }
    return description;
};

const trackPageView = (
    upgradedVariant: IProduct | null,
    rootProducts: readonly IProduct[],
    price: IAPIPrice | null,
    baseVariant: IProduct | null,
): void => {
    const skus: ReadonlyArray<IProductSKU> = upgradedVariant
        ? upgradedVariant.skus
        : rootProducts.reduce<ReadonlyArray<IProductSKU>>(
              (skuList, product) => skuList.concat(product.skus),
              [],
          );
    const priceValue = decimal(
        price
            ? getProductPrice(price, {
                  priceType: PriceType.COSMETIC_EXCL_TAX,
                  includePostDiscountAddons: true,
                  quantity: 1,
              })
            : ZERO,
    );
    trackProductValuePageView(skus, priceValue, "product", "ProductPageView");
    if (baseVariant) {
        trackViewItemEvent(baseVariant);
    }
};

const redirectToFavoritesPage = (favoritesLink: IWebPageURL): void => {
    setTimeout(() => {
        navigateToURL(favoritesLink);
    }, 0);
};

const buildAdditionalContentHelper = (
    buildAdditionalContent:
        | ((
              rootProduct: IProduct | null,
              optionSet: IModularConfiguratorOptionSet,
          ) => JSX.Element | null)
        | undefined,
    rootProduct: IProduct | null,
    optionSet: IModularConfiguratorOptionSet,
): JSX.Element | null => {
    if (!buildAdditionalContent || !rootProduct) {
        return null;
    }
    return buildAdditionalContent(rootProduct, optionSet);
};

export const Configurator = (props: IProps): React.ReactNode => {
    const {
        optionSet,
        title,
        preheader,
        subhead,
        description: descriptionProp,
        descriptions,
        promoCopy,
        liveChatHeader,
        chatLinkComponent,
        showImage,
        showShippingLead,
        financingLink,
        basketLink,
        favoritesLink,
        configureGiftsLink,
        showUpsellModal,
        showUpsellModalIcon,
        getUpsellModalComponentFn,
        buttonColor = "primary-congress-blue",
        enableHistoryInteraction,
        buildAdditionalContent,
        configType = "pdp-hero",
        getDeliveryCopy = () => null,
        showFeelScale = false,
        showPreTitle = false,
        learnMoreSelector = "#pdp-features",
        hideLearnMoreLink,
        showVariantCallout = false,
        starRatingURL,
        strikeThroughMSRP = false,
        actualPriceStyle = "",
        boxHeaderContent = "",
        topBadgeContent = "",
        overrideFinancingCopy = "",
        supDollarSign,
        enableAddToFavorites: enableAddToFavoritesProp,
        showStarRatingsModal,
        showFinancingModal,
        phoneNumber,
        showImagePerUpgradeOption = false,
        isPlpPanelStyle,
    } = props;

    // Redux selectors
    const state = useAppSelector(
        (rootState) => rootState.configurator || defaults,
    );
    const rootProducts = state.entities.rootProducts;
    const concreteBundles = state.entities.concreteBundles;
    const rootProduct = useAppSelector((rootState) =>
        rootProductSelector(rootState.configurator || defaults),
    );
    const baseVariant = useAppSelector((rootState) =>
        baseVariantSelector(rootState.configurator || defaults),
    );
    const selectedUpgradeID = state.ui.selectedUpgrade;
    const upgradedVariant = useAppSelector((rootState) =>
        upgradedVariantSelector(rootState.configurator || defaults),
    );
    const price = useAppSelector((rootState) =>
        upgradedVariantPriceSelector(rootState.configurator || defaults),
    );
    const financingPlans = state.entities.financingPlans;
    const quantity = state.ui.quantity;
    const selectedAddonVariants = useAppSelector((rootState) =>
        addonVariantsSelector(rootState.configurator || defaults),
    );
    const selectedAddonPrice = useAppSelector((rootState) =>
        addonVariantPricesSelector(rootState.configurator || defaults),
    );
    const addToBasketButtonText = state.ui.addToBasketButtonText;
    const addToFavoritesButtonText = state.ui.addToFavoritesButtonText;
    const optionSelectionErrorReason = state.ui.optionSelectionErrorReason;

    const postCode = useAppSelector((rootState) => {
        return rootState.common.enteredLocation &&
            isZipLocation(rootState.common.enteredLocation)
            ? rootState.common.enteredLocation.zip
            : rootState.common.detectedLocation &&
                rootState.common.detectedLocation.zip
              ? rootState.common.detectedLocation.zip
              : "";
    });

    // Dispatchers
    const dispatch = useAppDispatch();

    // Page settings
    const enableEcom = usePageSetting("enable_ecom");
    const enableAddToFavorites = !enableEcom || !!enableAddToFavoritesProp;

    // Memoized values
    const currentProductID = getCurrentProductID(rootProduct, optionSet);
    const description = getDescription(
        descriptions,
        descriptionProp,
        currentProductID,
    );

    // Load shipping methods function
    const loadShippingMethods = async (): Promise<void> => {
        const rootProductID = getRootProductID(rootProduct, optionSet);
        if (!postCode) {
            await dispatch(loadLocation());
        }
        dispatch(loadShippingMethod({ productID: rootProductID, postCode }));
    };

    // Update add to basket text function
    const updateAddToBasketText = (): void => {
        const isPreorder =
            upgradedVariant && isPreorderProduct(upgradedVariant);
        const newAddToBasketButtonText = isPreorder
            ? t`Pre-Order`
            : t`Add to Cart`;
        if (addToBasketButtonText !== newAddToBasketButtonText) {
            dispatch(
                setAddToBasketBtnState({
                    cooldownActive: false,
                    text: newAddToBasketButtonText,
                }),
            );
        }
    };

    // Add to favorites handlers
    const onBeforeAddToFavorites = (): void => {
        dispatch(setAddToFavoritesBtnState({ text: t`Saving...` }));
    };

    const onAfterAddToFavorites = (): void => {
        dispatch(setAddToFavoritesBtnState({ text: t`Saved!` }));
        setTimeout(() => {
            dispatch(setAddToFavoritesBtnState({ text: VIEW_FAVORITES }));
        }, 1000);
    };

    const onAddToFavorites = async (): Promise<void> => {
        if (addToFavoritesButtonText === VIEW_FAVORITES) {
            redirectToFavoritesPage(favoritesLink);
            return;
        }
        onBeforeAddToFavorites();
        if (upgradedVariant) {
            await addProductToWishList(LIST_FAVORITES, upgradedVariant.url);
        }
        for (const addon of selectedAddonVariants) {
            await addProductToWishList(
                LIST_FAVORITES,
                getProductURL(addon.productID),
            );
        }
        onAfterAddToFavorites();
    };

    // componentDidMount effect
    useEffect(() => {
        if (enableHistoryInteraction) {
            startHistoryListener(dispatch);
        }

        // Load main product from API
        if (optionSet.product_ids.length <= 0) {
            throw new Error("rootProductIDs array must not be empty");
        }
        dispatch(
            loadProducts({
                productIDs: optionSet.product_ids,
                initialVariantID: optionSet.initial_variant_id,
            }),
        );

        // Load Bundles
        const baseVariantID = baseVariant?.id || null;
        if (baseVariantID) {
            const selectedUpgradeID = getSelectedUpgradeIDFromURL();
            dispatch(
                loadConcreteBundles({
                    variantID: baseVariantID,
                    selectedUpgradeID: selectedUpgradeID,
                }),
            );
        }

        trackConfiguratorLoadedEvent();

        // Load shipping method
        loadShippingMethods();

        // Load financing details to display product-specific plans if available
        const productID =
            baseVariant?.id || optionSet.initial_variant_id || currentProductID;
        dispatch(loadFinancingPlans({ productID }));
    }, []);

    // componentDidUpdate effects - track previous values
    const prevBaseVariantID = React.useRef<IProductID | null>(null);
    const prevUpgradedVariantID = React.useRef<IProductID | null>(null);
    const prevQuantity = React.useRef<number>(quantity);
    const prevPostCode = React.useRef<string>(postCode);
    const prevPrice = React.useRef<IAPIPrice | null>(price);
    const prevSelectedUpgradeID = React.useRef<IProductID | null>(
        selectedUpgradeID,
    );
    const hasCompletedFirstRender = React.useRef(false);

    useEffect(() => {
        if (hasCompletedFirstRender.current) {
            showFinanceBlock();
        } else {
            hasCompletedFirstRender.current = true;
        }

        const nextBaseVariantID = baseVariant?.id || null;
        const nextUpgradedVariantID = upgradedVariant?.id || null;
        const baseVariantChanged =
            prevBaseVariantID.current !== nextBaseVariantID;
        const upgradedVariantChanged =
            prevUpgradedVariantID.current !== nextUpgradedVariantID;
        const qtyChanged = prevQuantity.current !== quantity;
        const selectedUpgradeInConfigurator =
            selectedUpgradeID &&
            rootProducts
                .map((product: IProduct) => product.id)
                .includes(selectedUpgradeID);
        const selectedUpgradeChanged =
            selectedUpgradeID &&
            selectedUpgradeID !== prevSelectedUpgradeID.current;

        // If the base variant changed, reload the bundle and financing data for the new variant
        if (baseVariantChanged && nextBaseVariantID) {
            const selectedUpgradeID = getSelectedUpgradeIDFromURL();
            dispatch(
                loadConcreteBundles({
                    variantID: nextBaseVariantID,
                    selectedUpgradeID: selectedUpgradeID,
                }),
            );
            dispatch(loadFinancingPlans({ productID: nextBaseVariantID }));
            // Track variant changes for pdp-hero configurator, see: #18585
            if (configType === "pdp-hero") {
                trackVariantSelectedEvent(nextBaseVariantID);
            }
        }

        // Update the page URL to include the new selection data
        if (
            enableHistoryInteraction &&
            (baseVariantChanged || upgradedVariantChanged)
        ) {
            pushPDPVariantURLChange(
                rootProduct,
                baseVariant,
                selectedUpgradeID,
            );
            // Refire Listrak AddProductBrowse activity only when the selected variant is changed.
            if (
                prevBaseVariantID.current &&
                baseVariant &&
                baseVariant.skus.length > 0
            ) {
                trackListrackProductBrowse(baseVariant.skus[0]);
            }
        }

        // Load the price data for quantities > 1
        if (
            nextUpgradedVariantID &&
            (upgradedVariantChanged || qtyChanged) &&
            quantity > 1
        ) {
            dispatch(loadPrice({ productID: nextUpgradedVariantID, quantity }));
        }

        // Update add to basket button text?
        if (upgradedVariantChanged && upgradedVariant) {
            updateAddToBasketText();
        }

        // Reload shipping methods if zip code changed
        if (prevPostCode.current !== postCode) {
            loadShippingMethods();
        }

        // Call Google remarketing analytics after products are loaded
        // prices update to 0 briefly during load, filter out a null price
        if (price && prevPrice.current !== price && configType === "pdp-hero") {
            trackPageView(upgradedVariant, rootProducts, price, baseVariant);
        }

        // If selectedUpgrade has changed, and upgrade is selectable in configurator, select it
        if (selectedUpgradeChanged && selectedUpgradeInConfigurator) {
            const product = rootProducts.filter(
                (p) => p.id === selectedUpgradeID,
            )[0];
            dispatch(setSelectedRootProduct(product));
        }
        // If selecting upsell changes rootproduct to match, clear selectedUpgrade
        if (
            rootProduct &&
            selectedUpgradeID &&
            rootProduct.id === selectedUpgradeID
        ) {
            dispatch(setSelectedUpgrade(null));
            pushPDPVariantURLChange(rootProduct, baseVariant, null);
        }

        // Update refs for next render
        prevBaseVariantID.current = nextBaseVariantID;
        prevUpgradedVariantID.current = nextUpgradedVariantID;
        prevQuantity.current = quantity;
        prevPostCode.current = postCode;
        prevPrice.current = price;
        prevSelectedUpgradeID.current = selectedUpgradeID;
    });

    return (
        <Component
            optionSet={optionSet}
            title={title}
            preheader={preheader}
            subhead={subhead}
            description={description}
            financingLink={financingLink}
            rootProduct={rootProduct}
            baseVariant={baseVariant}
            upgradedVariant={upgradedVariant}
            price={price}
            financingPlans={financingPlans}
            concreteBundles={concreteBundles}
            showImage={showImage}
            promoCopy={promoCopy}
            chatLinkComponent={chatLinkComponent}
            liveChatHeader={liveChatHeader}
            basketLink={basketLink}
            configureGiftsLink={configureGiftsLink}
            showShippingLead={showShippingLead}
            selectedAddonPrice={selectedAddonPrice}
            enableAddToFavorites={enableAddToFavorites}
            addToFavoritesButtonText={addToFavoritesButtonText}
            onAddToFavorites={onAddToFavorites}
            showUpsellModal={showUpsellModal}
            showUpsellModalIcon={showUpsellModalIcon}
            getUpsellModalComponentFn={getUpsellModalComponentFn}
            buttonColor={buttonColor}
            configType={configType}
            getDeliveryCopy={getDeliveryCopy}
            showFeelScale={showFeelScale}
            showPreTitle={showPreTitle}
            learnMoreSelector={learnMoreSelector}
            hideLearnMoreLink={hideLearnMoreLink}
            showVariantCallout={showVariantCallout}
            starRatingURL={starRatingURL}
            strikeThroughMSRP={strikeThroughMSRP}
            actualPriceStyle={actualPriceStyle}
            boxHeaderContent={boxHeaderContent}
            topBadgeContent={topBadgeContent}
            overrideFinancingCopy={overrideFinancingCopy}
            supDollarSign={supDollarSign}
            showStarRatingsModal={showStarRatingsModal}
            showFinancingModal={showFinancingModal}
            phoneNumber={phoneNumber}
            optionSelectionErrorReason={optionSelectionErrorReason}
            showImagePerUpgradeOption={showImagePerUpgradeOption}
            isPlpPanelStyle={isPlpPanelStyle}
        >
            {buildAdditionalContentHelper(
                buildAdditionalContent,
                rootProduct,
                optionSet,
            )}
        </Component>
    );
};
